import type { EventModel } from '@bryntum/scheduler-thin';
import type { DomConfig } from '@bryntum/core-thin';
import { formatTime24h } from '../../../../../utils/dateUtils';
import type { OpenShiftState } from '../../../components/Shifts/Common/types';
import './eventRenderer.css';

export type OpenShiftLabels = {
  stateLabels: Record<OpenShiftState, string>;
  pendingLabels: Record<string, string>;
};

interface ShiftEventRecord {
  startDate: string | Date;
  endDate: string | Date;
  name: string;
}

const timeRangeRow = (record: ShiftEventRecord): DomConfig => ({
  class: 'd-flex fw-semibold',
  children: [
    {
      class: 'text-truncate',
      text: `${formatTime24h(record.startDate)} – ${formatTime24h(
        record.endDate,
      )}`,
    },
  ],
});

const shiftNameRow = (name: string): DomConfig => ({
  class: 'text-truncate',
  style: 'font-size: 0.7rem',
  text: name,
});

const targetNameRow = (targetName: string): DomConfig => ({
  class: 'text-truncate opacity-75',
  style: 'font-size: 0.7rem',
  text: `@ ${targetName}`,
});

const stateBadge = (
  state: OpenShiftState,
  labels: OpenShiftLabels,
): DomConfig => {
  return {
    class: `text-truncate open-shift-badge open-shift-badge--${state}`,
    style: 'font-size: 0.65rem; font-weight: 600',
    text: labels.stateLabels[state],
  };
};

const pendingBadge = (action: string, labels: OpenShiftLabels): DomConfig => ({
  class: 'text-truncate open-shift-badge open-shift-badge--pending',
  style: 'font-size: 0.65rem; font-weight: 600; font-style: italic',
  text: labels.pendingLabels[action] ?? action,
});

const renderOpenShiftEvent = (
  eventRecord: EventModel,
  labels: OpenShiftLabels,
): DomConfig[] => {
  const rows: DomConfig[] = [timeRangeRow(eventRecord)];

  const pending = eventRecord.getData('pendingAction');
  if (pending) {
    rows.push(pendingBadge(pending, labels));
  } else {
    const state = eventRecord.getData('openShiftState');
    rows.push(stateBadge(state, labels));
  }

  const targetName = eventRecord.getData('targetName');
  if (targetName) {
    rows.push(targetNameRow(targetName));
  }

  return rows;
};

export const renderShiftEvent = (
  eventRecord: EventModel,
  labels: OpenShiftLabels | null,
): DomConfig[] => {
  if (eventRecord.getData('isOpenShift') && labels) {
    return renderOpenShiftEvent(eventRecord, labels);
  }

  const rows: DomConfig[] = [timeRangeRow(eventRecord)];

  if (eventRecord.name) {
    rows.push(shiftNameRow(eventRecord.name));
  }

  const targetName = eventRecord.getData('targetName');

  if (targetName) {
    rows.push(targetNameRow(targetName));
  }

  return rows;
};
